import { Divider, PageFooter, Text, Title } from "@clipboard-health/ui-components";
import { Link, Skeleton, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { Button } from "@src/appV2/redesign/components/Button";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";

import { MissedBadgeCounter } from "./MissedBadgeCounter";

interface WorkBadgeReviewPolicyRedesignProps {
  onClose: () => void;
  onNeedNewBadge: () => void;
  unverifiedBadgeCount: number;
  hasWorkedBadgeRequiredShift: boolean;
  isLoading?: boolean;
}

function buildVerificationSteps(onNeedNewBadge: () => void) {
  return [
    {
      number: 1,
      title: "Order a badge before your shift",
      description: (
        <Stack spacing={4}>
          <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
            Create one from your{" "}
            <Link
              component="button"
              variant="body2"
              sx={{ verticalAlign: "baseline" }}
              onClick={onNeedNewBadge}
            >
              account page
            </Link>
            . You can order a plastic badge or print one at home
          </Text>
          <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
            If you've arrived at the workplace and forgot your badge, ask the workplace for a
            temporary one.
          </Text>
        </Stack>
      ),
    },
    {
      number: 2,
      title: "Verify your badge",
      description: (
        <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
          Before you clock in for your shift, take a selfie with your full badge on your work attire
          and upload it in the app.
        </Text>
      ),
    },
    {
      number: 3,
      title: "Keep it on throughout the shift",
      description: (
        <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
          When you start your shift, make sure your full name and role are on the badge, and wear it
          over your work attire
        </Text>
      ),
    },
  ];
}

export function WorkBadgeReviewPolicyRedesign(props: WorkBadgeReviewPolicyRedesignProps) {
  const {
    onClose,
    onNeedNewBadge,
    unverifiedBadgeCount,
    hasWorkedBadgeRequiredShift,
    isLoading = false,
  } = props;

  return (
    <>
      <StickyScrollPageHeader title="" headerVariant="tertiary" onClickBack={onClose} />

      <PageContent>
        <Stack spacing={8} sx={{ paddingTop: 4 }}>
          <Stack spacing={3}>
            <Title component="h3">Wear your badge</Title>
            <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
              Wearing a badge builds trust and shows staff and residents who you are
            </Text>
          </Stack>

          {isLoading && (
            <Skeleton
              variant="rounded"
              height={180}
              sx={(theme) => ({ borderRadius: theme.borderRadius?.medium })}
            />
          )}

          {!isLoading && hasWorkedBadgeRequiredShift && (
            <>
              <MissedBadgeCounter
                unverifiedBadgeCount={unverifiedBadgeCount}
                hasWorkedBadgeRequiredShift={hasWorkedBadgeRequiredShift}
              />

              <Divider />
            </>
          )}

          <Stack spacing={5}>
            <Title
              component="h2"
              variant="h5"
              sx={(theme) => ({
                color: theme.palette.text.secondary,
              })}
            >
              Badge Policy
            </Title>
            <Stack spacing={5}>
              <Text variant="body2">
                You must wear a badge on your work attire during shifts at workplaces that require
                one.
              </Text>
              <Text variant="body2">The badge should display your full name and role.</Text>
              <Text variant="body2">
                If you miss 3 badge verifications in 60 days, you won't be able to pick up shifts
                that require a badge for 30 days.
              </Text>
            </Stack>
          </Stack>

          <Divider />

          <Stack spacing={4}>
            <Title
              component="h2"
              variant="h5"
              sx={(theme) => ({
                color: theme.palette.text.secondary,
              })}
            >
              How to get badge verified
            </Title>
            <Stack spacing={8}>
              {buildVerificationSteps(onNeedNewBadge).map((step) => (
                <Stack key={step.number} direction="row" spacing={3} alignItems="flex-start">
                  <Text bold variant="body1" sx={{ minWidth: "1.25rem", textAlign: "center" }}>
                    {step.number}
                  </Text>
                  <Stack spacing={2}>
                    <Text bold variant="body1">
                      {step.title}
                    </Text>
                    {step.description}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Stack>
      </PageContent>

      <PageFooter>
        <Stack alignItems="center" spacing={4} width="100%">
          <Button
            fullWidth
            size="large"
            type="submit"
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.WORK_BADGE_NEED_NEW_BADGE_CLICKED);
              onNeedNewBadge();
            }}
          >
            Get a badge
          </Button>
        </Stack>
      </PageFooter>
    </>
  );
}
